import JSZip from 'jszip'
import XLSX from 'xlsx'

const tableReadme = `This data download contains two data files. The file named "OECD Data Kitchen - Chart" includes the data for the countries, breakdowns and periods you selected. The file named "OECD Data Kitchen - All" contains all of the data covered by the OECD Data Kitchen for this indicator. When downloading in the Compare mode, there is a pair of data files for each indicator selected: x axis ("main"), y axis ("pair") and size ("size").

# NOTES AND DISCLAIMERS
[CMS]

If you have any comments or questions, please contact goingdigitaltoolkit@oecd.org.

Thank you for using the OECD Data Kitchen.

Data source
=================
[URL]

Date of retrieval
=================
[DATE]`

const imageReadme = `# NOTES AND DISCLAIMERS
[CMS]

If you have any comments or questions, please contact goingdigitaltoolkit@oecd.org.

Thank you for using the OECD Data Kitchen.

Chart URL
=================
[URL]

Date of retrieval
=================
[DATE]`

import { getData, getDataConstraints, getPNG } from '@/services/api.js'
import {
  dataToTable,
  dataToChartTable,
  getAvailableDates,
  getAvailableCountries,
  extendDataWithMeta,
  extendDataWithConfig,
  rewriteDataDimensionsTypes,
  getTimeScales,
  getUnits,
} from '@/utils/cube.js'

const openSockets = []
function getSocket(slot, type = 'data') {
  return openSockets.find(s => s.slot === slot && s.type === type)
}
function dropSocket(slot, type = 'data') {
  openSockets.splice(
    openSockets.findIndex(s => s.slot === slot && s.type === type),
    1
  )
}

export default {
  state: {
    main: null,
    pair: null,
    size: null,
    mainConstraints: null,
    pairConstraints: null,
    sizeConstraints: null,
  },
  getters: {
    mainDataAsTable(state) {
      return state.main ? dataToTable(state.main) : null
    },
    pairDataAsTable(state) {
      return state.pair ? dataToTable(state.pair) : null
    },
    sizeDataAsTable(state) {
      return state.size ? dataToTable(state.size) : null
    },
    mainChartData(state) {
      return state.main ? dataToChartTable(state.main, 'en') : []
    },
    pairChartData(state) {
      return state.pair ? dataToChartTable(state.pair, 'en') : []
    },
    sizeChartData(state) {
      return state.size ? dataToChartTable(state.size, 'en') : []
    },
    mainAvailableDates(state) {
      return state.main ? getAvailableDates(state.main).sort((a, b) => a - b) : []
    },
    pairAvailableDates(state) {
      return state.pair ? getAvailableDates(state.pair).sort((a, b) => a - b) : []
    },
    sizeAvailableDates(state) {
      return state.size ? getAvailableDates(state.size).sort((a, b) => a - b) : []
    },
    mainAvailableCountries(state) {
      return state.main ? getAvailableCountries(state.main) : []
    },
    pairAvailableCountries(state) {
      return state.pair ? getAvailableCountries(state.pair) : []
    },
    sizeAvailableCountries(state) {
      return state.size ? getAvailableCountries(state.size) : []
    },
    mainTimeScales(state) {
      return state.main ? getTimeScales(state.main) : []
    },
    pairTimeScales(state) {
      return state.pair ? getTimeScales(state.pair) : []
    },
    sizeTimeScales(state) {
      return state.size ? getTimeScales(state.size) : []
    },
    mainUnits(state) {
      return state.main ? getUnits(state.main) : []
    },
    pairUnits(state) {
      return state.pair ? getUnits(state.pair) : []
    },
    sizeUnits(state) {
      return state.size ? getUnits(state.size) : []
    },
    mainConstraints(state) {
      return state.mainConstraints?.contentConstraints[0].cubeRegions[0].keyValues
    },
    pairConstraints(state) {
      return state.pairConstraints?.contentConstraints[0].cubeRegions[0].keyValues
    },
    sizeConstraints(state) {
      return state.sizeConstraints?.contentConstraints[0].cubeRegions[0].keyValues
    },
    annotations(state) {
      return Object.keys(state).reduce((acum, key) => {
        if (state[key] !== null) acum[key] = state[key].structures[0].annotations
        return acum
      }, {})
    },
    power: state => slot => {
      try {
        return Math.pow(
          10,
          Number(
            state[slot].structures[0].attributes.dataSet.find(d => d.id === 'UNIT_MULT').values[0]
              .id
          )
        )
      } catch (err) {
        try {
          return Math.pow(
            10,
            Number(
              state[slot].structures[0].attributes.dimensionGroup.find(d => d.id === 'UNIT_MULT')
                .values[0].id
            )
          )
        } catch (err) {
          return 1
        }
      }
    },
  },
  mutations: {
    mainData(state, data) {
      state.main = data
    },
    pairData(state, data) {
      state.pair = data
    },
    sizeData(state, data) {
      state.size = data
    },
    mainConstraints(state, data) {
      state.mainConstraints = data
    },
    pairConstraints(state, data) {
      state.pairConstraints = data
    },
    sizeConstraints(state, data) {
      state.sizeConstraints = data
    },
    rewriteDataDimensionsTypes(state, params) {
      state[params.slot] = rewriteDataDimensionsTypes(state[params.slot], params.changes)
    },
    wipeData(state) {
      state.main = null
      state.pair = null
      state.size = null
    },
  },
  actions: {
    getData(_, { slot, meta, config, cubeId, cubeVersion, dimensions, flip }) {
      const openSocket = getSocket(slot)

      if (openSocket) {
        openSocket.abort()
        dropSocket(slot)
      }

      const [request, abort] = getData({
        cubeId,
        cubeVersion,
        dimensions,
        cubeSource: config.cubeSource,
      })
      openSockets.push({ type: 'data', slot, abort })

      return request
        .then(res => {
          dropSocket(slot)
          if (getSocket(slot)) throw new Error('Discarded data request')
          const data = extendDataWithMeta(
            extendDataWithConfig(res.data, { meta, config, flip }),
            meta
          )
          return data
        })
        .catch(err => console.error(err))
    },
    getDataConstraints(_, { slot, cubeId, cubeSource, cubeVersion, dimensions, fromYear }) {
      const openSocket = getSocket(slot, 'constraints')

      if (openSocket) {
        openSocket.abort()
        dropSocket(slot, 'constraints')
      }

      const [req, abort] = getDataConstraints({
        cubeId,
        cubeVersion,
        cubeSource,
        dimensions,
        fromYear,
      })
      openSockets.push({ type: 'constraints', slot, abort })

      return req
        .then(res => {
          dropSocket(slot, 'constraints')
          if (getSocket(slot, 'constraints')) throw new Error('Discarded constraints requests')
          return res.data
        })
        .catch(err => console.error(err))
    },
    exportCSV(context, params) {
      function dataToCsv(data) {
        data = columnFilter(data)
        let csvData = data.map(row => row.map(d => `"${d}"`).join(',')).join('\n')
        let b = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), csvData], {
          type: 'text/csv;charset=utf-8;',
        })
        return b
      }

      const zip = new JSZip()
      if (params.pairData) {
        zip.file('OECD DataKitchen - Chart (main).csv', dataToCsv(params.mainData))
        zip.file('OECD DataKitchen - All (main).csv', dataToCsv(context.getters.mainDataAsTable))
        zip.file('OECD DataKitchen - Chart (pair).csv', dataToCsv(params.pairData))
        zip.file('OECD DataKitchen - All (pair).csv', dataToCsv(context.getters.pairDataAsTable))
        if (params.sizeData) {
          zip.file('OECD DataKitchen - Chart (size).csv', dataToCsv(params.sizeData))
          zip.file('OECD DataKitchen - All (size).csv', dataToCsv(context.getters.sizeDataAsTable))
        }
      } else {
        zip.file('OECD DataKitchen - Chart.csv', dataToCsv(params.mainData))
        zip.file('OECD Data Kitchen - All.csv', dataToCsv(context.getters.mainDataAsTable))
      }
      zip.file('readme.txt', parseDisclaimerNotes(params.disclaimers, 'table'))
      return zip.generateAsync({
        type: 'blob',
      })
    },
    exportXLSX(context, params) {
      function dataToWB(data) {
        data = columnFilter(data)
        const table =
          '<table><tbody>' +
          data.map(row => {
            return '<tr>' + row.map(value => `<td>${value}</td>`) + '</tr>'
          }) +
          '</tbody></table>'
        const renderer = document.createElement('template')
        renderer.innerHTML = table
        const wb = XLSX.utils.table_to_book(renderer.content.children[0], {
          sheet: 'Indicator',
        })
        const bytes = XLSX.write(wb, {
          bookType: 'xlsx',
          bookSST: true,
          type: 'base64',
          sheet: 'data',
        })

        return bytes
      }
      const zip = new JSZip()
      if (params.pairData) {
        zip.file('OECD DataKitchen - Chart (main).xlsx', dataToWB(params.mainData), {
          base64: true,
          binary: true,
        })
        zip.file('OECD DataKitchen - All (main).xlsx', dataToWB(context.getters.mainDataAsTable), {
          base64: true,
          binary: true,
        })
        zip.file('OECD DataKitchen - Chart (pair).xlsx', dataToWB(params.pairData), {
          base64: true,
          binary: true,
        })
        zip.file('OECD DataKitchen - All (pair).xlsx', dataToWB(context.getters.pairDataAsTable), {
          base64: true,
          binary: true,
        })
        if (params.sizeData) {
          zip.file('OECD DataKitchen - Chart (size).xlsx', dataToWB(params.sizeData), {
            base64: true,
            binary: true,
          })
          zip.file(
            'OECD DataKitchen - All (size).xlsx',
            dataToWB(context.getters.sizeDataAsTable),
            {
              base64: true,
              binary: true,
            }
          )
        }
      } else {
        zip.file('OECD DataKitchen - Chart.xlsx', dataToWB(params.mainData), {
          base64: true,
          binary: true,
        })
        zip.file('OECD DataKitchen - All.xlsx', dataToWB(context.getters.mainDataAsTable), {
          base64: true,
          binary: true,
        })
      }
      zip.file('readme.txt', parseDisclaimerNotes(params.disclaimers, 'table'))
      return zip.generateAsync({
        type: 'blob',
      })
    },
    exportPNG(_, params) {
      const zip = new JSZip()
      const hash = params.url.split('#')[1].replace(/\/(explore|embedded)\//, '/print/')
      return getPNG(hash).then(blob => {
        zip.file('oecd-indicator.png', blob, {
          binary: true,
        })
        zip.file('readme.txt', parseDisclaimerNotes(params.disclaimers, 'image'))
        return zip.generateAsync({
          type: 'blob',
        })
      })
    },
    exportSVG(_, params) {
      const zip = new JSZip()
      zip.file(
        'oecd-indicator.svg',
        new Blob([params.source], {
          type: 'text/xml+svg',
        })
      )
      zip.file('readme.txt', parseDisclaimerNotes(params.disclaimers))
      return zip.generateAsync({
        type: 'blob',
      })
    },
  },
}

function parseDisclaimerNotes(notes, format) {
  const cms = notes.map(n => '- ' + n.value).join('\n\n')
  const today = new Date()

  const printData = today.toLocaleDateString('en-GB')

  let text
  if (format === 'table') {
    text = tableReadme.replace(/\[CMS\]/, cms)
  } else {
    text = imageReadme.replace(/\[CMS\]/, cms)
  }
  text = text.replace(/\[URL\]/, location.href).replace(/\[DATE\]/, `${printData}`) //`${day}/${month}/${year}`)

  const blob = new Blob([text], {
    type: 'text/plain',
  })

  return blob
}

function columnFilter(data) {
  const colNames = data[0]
  const rpi = colNames.indexOf('Reference period')
  const umi = colNames.indexOf('Unit multiplier')
  let umValue = new RegExp('^units$', 'i')
  const colFilter = colNames.map((_, i) => i !== rpi && i !== umi)

  data.slice(1).forEach(row => {
    if (rpi >= 0) {
      colFilter[rpi] =
        colFilter[rpi] || !(row[rpi] === null || row[rpi] === '' || row[rpi] === void 0)
    }

    if (umi >= 0) {
      colFilter[umi] =
        colFilter[umi] ||
        (!umValue.test(row[umi]) && !(row[umi] === null || row[umi] === '' || row[umi] === void 0))
    }
  })

  return [data[0].filter((_, i) => colFilter[i])].concat(
    data.slice(1).map(row => {
      return row.filter((_, i) => colFilter[i])
    })
  )
}
